<template>
  <div>
    <div class="d-flex">
      <div class="flex-grow-1 d-flex align-center" data-cy="podFiles-mediaLegal-title">
        <h1>{{ $t('routes.pod.mediaLegal') }}</h1>
      </div>

      <div v-if="$canUploadFileAndAsset() && getCurrentNode" class="flex-grow-0">
        <UploadFilesDialog
          v-model="uploadAssetsDialogVisible"
          :node="getCurrentNode"
          :pod="pod"
          :uploadDialogType="'legal'"
        >
          <template v-slot:activator="{ props }">
            <v-btn v-bind="props"
                   color="primary"
                   class="mr-1"
                   elevation="0"
                   data-cy="podFiles-mediaLegal-uploadDocuments-btn">
              <v-icon>mdi-plus-circle</v-icon>
              {{ $t('routes.pod.uploadDocuments') }}
            </v-btn>
          </template>
        </UploadFilesDialog>
      </div>
    </div>

    <div>
      <div v-for="categoryType in getAvailableCategories" :key="categoryType.id" class="mt-4">
        <h3>{{ categoryType.text }}</h3>

        <div
          v-for="asset in getAssetsInCategory(categoryType.id)" :key="asset.id"
          v-on:contextmenu.stop.prevent="contextMenuClick($event, asset)"
          class="prevent-select mt-1 d-flex align-center pod-asset-list-item"
          style=""
        >
          <!-- Asset name -->
          <div class="flex-grow-1">

            <div class="d-flex">
              <div class="d-flex align-center mr-2">
                <v-icon v-if="asset.broken">
                  mdi-image-broken
                </v-icon>

                <v-tooltip v-if="!asset.broken && isSystemGeneratedFile(asset)" location="bottom" :aria-label="$t('routes.pod.thisIsSystemFile')">
                  <template v-slot:activator="{ props }">
                    <img
                      v-bind="props"
                      :src="getSystemGeneratedIcon"
                      style="width: 20px; height: 20px;"
                    />
                  </template>
                  <span>{{ $t('routes.pod.thisIsSystemFile') }}</span>
                </v-tooltip>
              </div>
              <div class="flex-grow-1" data-cy="podFiles-mediaAssets-assetName">
                <span style="word-wrap: anywhere; white-space: pre-line; font-weight: 600;">
                  {{ asset.name }}
                </span>

                <!-- Status or clarification -->
                <div v-if="asset.clarification != null"
                     data-cy="podFiles-mediaAssets-clarification">
                  <v-tooltip location="bottom" :aria-label="asset.clarification">
                    <template v-slot:activator="{ props }">
                      <small v-bind="props">
                        <span v-if="asset.state !== podStates.COMPLETE ">{{ assetState(asset) }}</span>
                        <span v-else>{{ $t('menus.uploadFiles.legalCategoryExpiryDate')}}: {{ asset.legalexpirydate ? formatDate(asset.legalexpirydate) : "-" }}</span>
                      </small>
                    </template>
                    <span> {{ asset.clarification }} </span>
                  </v-tooltip>
                </div>
                <div v-else data-cy="podFiles-mediaAssets-state">
                  <small>
                    <span v-if="asset.state !== podStates.COMPLETE ">{{ assetState(asset) }}</span>
                    <span v-else :style="getExpirationDateStyle(asset)">
                      {{ $t('menus.uploadFiles.legalCategoryExpiryDate')}}: {{ asset.legalexpirydate ? formatDate(asset.legalexpirydate) : "-" }}
                    </span>
                  </small>
                </div>

              </div>
            </div>

          </div>


          <!-- Size -->
          <div class="text-center">
            <small>{{ formatSize((asset && asset.size) ? asset.size : 0, 2) }}</small>
            <br/>
            <!-- Mimetype -->
            <small>{{ asset.mimetype }}</small>
          </div>

          <!-- Three-dot menu -->
          <div class="ml-2">
            <v-btn
              @click="contextMenuBtnClick($event, asset)"
              variant="text"
              rounded="circle"
              width="36"
              height="36"
              icon
              data-cy="podFiles-mediaAssets-actions-btn"
              :aria-label="$t('common.options')"
            >
              <v-icon>mdi-dots-vertical</v-icon>
            </v-btn>
          </div>


        </div>
      </div>

      <LegalContextMenu
        v-if="contextMenuAsset"
        v-model="showingAssetContextMenu"
        :positionX="contextMenuX"
        :positionY="contextMenuY"
        :pod="pod"
        :asset="contextMenuAsset"
      />
    </div>

    <div class="mt-8">
      <div>
        <h3>{{ $t('routes.pod.rightsSummary') }}</h3>
      </div>

      <v-form ref="form" @submit.prevent="saveRightsSummary" :disabled="isFormReadonly">
        <div class="mt-2">
          <v-text-field
            v-model="owner"
            :label="$t('routes.pod.owner')"
            :rules="textInputRules"
            variant="solo-filled"
            flat
          />

          <v-text-field
            v-model="typeOfRights"
            :label="$t('routes.pod.typeOfRights')"
            :rules="textInputRules"
            variant="solo-filled"
            flat
          />

          <v-text-field
            v-model="permittedUses"
            :label="$t('routes.pod.permittedUses')"
            :rules="textInputRules"
            variant="solo-filled"
            flat
          />

          <v-text-field
            v-model="territory"
            :label="$t('routes.pod.territory')"
            :rules="textInputRules"
            variant="solo-filled"
            flat
          />

          <v-date-input
            v-model="expiryDate"
            :label="$t('routes.pod.expirationDate')"
            :aria-label="$t('routes.pod.expirationDate')"
            append-inner-icon="mdi-calendar"
            prepend-icon=""
            variant="solo-filled"
            required
            block
            flat
            data-cy="----------"
          />

          <v-textarea
            v-model="restrictions"
            :label="$t('routes.pod.restrictions')"
            variant="solo-filled"
            rows="3"
            flat
          />
        </div>

        <div>
          <div v-show="successMessage" class="bg-success pa-1 mt-1" data-cy="podMenu-tools-">{{ successMessage }}</div>
          <div v-show="errorMessage" class="bg-error pa-1 mt-1" data-cy="podMenu-tools-">{{ errorMessage }}</div>
        </div>

        <div v-if="!isFormReadonly" class="d-flex justify-center">
          <v-btn color="primary" type="submit" variant="flat" class="mt-4">
            {{ $t('common.save') }}
          </v-btn>
        </div>
      </v-form>
    </div>
  </div>
</template>

<script>
import { usePodLegalStore } from '@/stores/PodLegalStore.js';
import { useCommonStore } from '@/stores/CommonStore.js';
import { useNodeStore } from '@/stores/NodeStore.js';
import { usePodStore } from '@/stores/PodStore.js';
import { UTCDate } from '@date-fns/utc';
import { set as dateSet } from 'date-fns/set';
import { formatSize, formatDate, formatDateTimeMilliseconds } from '@/components/formatters.js';
import UploadFilesDialog from '@/routes/nodes2/components/dialogs/UploadFilesDialog.vue';
import LegalContextMenu from './LegalContextMenu.vue';

export default {
  name: "LegalToolbar",
  components: {
    UploadFilesDialog,
    LegalContextMenu,
  },
  data() {
    return {
      uploadAssetsDialogVisible: false,
      showingAssetContextMenu: false,
      contextMenuAsset: null,
      contextMenuX: 0,
      contextMenuY: 0,

      errorMessage: "",
      successMessage: "",

      owner: '',
      typeOfRights: '',
      permittedUses: '',
      territory: '',
      restrictions: '',

      datepickerMenu: false,
      // expiryDateValue: new Date(),
      expiryDateValue: null,
      expiryDateRules: [
        // v => !!v || this.$t('routes.pod.menuAccesslink.expiryDateRequired')
      ],

      textInputRules: [
        v => !v || v.length < 190 || this.$t('routes.pod.textIsTooLong'),
      ],
    };
  },
  mounted() {
    this.refreshPodLegal();

    this.errorMessage = "";
    this.successMessage = "";
  },
  watch: {
    getPodLegal(newValue) {
      this.owner = newValue.owner;
      this.typeOfRights = newValue.typeOfRights;
      this.permittedUses = newValue.permittedUses;
      this.territory = newValue.territory;
      this.restrictions = newValue.restrictions;

      this.expiryDateValue = newValue.expirationDate ? new UTCDate(newValue.expirationDate) : null;
    }
  },
  methods: {
    formatSize,
    formatDate,
    async refreshPodLegal() {
      if (this.pod) {
        const podlegalStore = usePodLegalStore();
        await podlegalStore.refreshPodLegal(this.pod.id);
      }
    },
    contextMenuClick(event, asset) {
      this.showingAssetContextMenu = false;

      this.contextMenuX = event.clientX;
      this.contextMenuY = event.clientY;
      this.contextMenuAsset = asset;

      this.$nextTick(() => {
        this.showingAssetContextMenu = true;
      });
    },
    contextMenuBtnClick(event, asset) {
      this.showingAssetContextMenu = false;

      this.contextMenuX = event.clientX - event.offsetX + event.target.offsetWidth;
      this.contextMenuY = event.clientY - event.offsetY + event.target.offsetHeight;
      this.contextMenuAsset = asset;

      this.$nextTick(() => {
        this.showingAssetContextMenu = true;
      });
    },
    async saveRightsSummary() {
      this.errorMessage = "";
      this.successMessage = "";

      const { valid } = await this.$refs.form.validate();

      if (!valid || this.isFormReadonly) {
        return;
      }

      const formData = {
        owner: this.owner,
        typeOfRights: this.typeOfRights,
        permittedUses: this.permittedUses,
        territory: this.territory,
        restrictions: this.restrictions,
        // expirationDate: this.expiryDate,
      }

      if (this.expiryDate) {
        formData.expirationDate = formatDateTimeMilliseconds(dateSet(new UTCDate(this.expiryDate), {
          hours: 23,
          minutes: 59,
          seconds: 59,
          milliseconds: 0,
        }))
      }

      const podlegalStore = usePodLegalStore();
      try {
        await podlegalStore.savePodLegal(this.pod.id, formData);
        // console.log("res", res);
        this.successMessage = this.$t('routes.pod.legalInformationSaved');
      } catch (error) {
        if (error && error.response && error.response.data && error.response.data.error) {
          this.errorMessage = error.response.data.error.message;
        } else {
          this.errorMessage = "Error!";
          console.log(error);   // unhandled error
        }
      }
      // Form is valid, proceed with saving
    },
  },
  computed: {
    getCurrentNode() {
      const nodeStore = useNodeStore();
      return nodeStore.getCurrentNode;
    },
    pod() {
      const podStore = usePodStore();
      return podStore.getViewPod;
    },
    getPodLegal() {
      const podlegalStore = usePodLegalStore();
      const podLegal = podlegalStore.podlegal;

      if (this.pod && podLegal && this.pod.id === podLegal.podid) {
        return podLegal;
      }

      return null;
    },
    getLegalAssets() {
      let assets = [];

      for (const assetType in this.pod.asset) {
        if (Object.prototype.hasOwnProperty.call(this.pod.asset, assetType)) {
          for (const asset of this.pod.asset[assetType]) {
            if (asset.legalcategory) {
              assets.push(asset);
            }
          }
        }
      }

      return assets;
    },
    getAvailableCategories() {
      const legalCategoryIds = this.getLegalAssets.map(x => x.legalcategory);
      const uniqueCategoryIds = new Set(legalCategoryIds);

      return Array.from(uniqueCategoryIds).map(x => {
        switch (x) {
          case 1: return {id: 1, text: this.$t('menus.uploadFiles.legalCategoryQuitClaim')};
          case 2: return {id: 2, text: this.$t('menus.uploadFiles.legalCategoryRightsAgreement')};
          case 3: return {id: 3, text: this.$t('menus.uploadFiles.legalCategoryReleaseForm')};
          case 4: return {id: 4, text: this.$t('menus.uploadFiles.legalCategoryOther')};
          default: return null; // or handle unknown categories appropriately
        }
      })
      .filter(Boolean) // Remove any null/undefined values
      .sort((a, b) => a.id - b.id); // Sort by id
    },
    getAssetsInCategory() {
      return function(categoryId) {
        return this.getLegalAssets.filter(x => x.legalcategory === categoryId);
      }
    },

    assetTypes: function() {
      const commonStore = useCommonStore();
      return commonStore.assetTypes;
    },
    isSystemGeneratedFile: function() {
      const systemTypes = [
        this.assetTypes.THUMBNAIL, this.assetTypes.LIVEPREVIEW,
        this.assetTypes.FRAME, this.assetTypes.RAW,
        this.assetTypes.AUDIO, this.assetTypes.WEB,
        this.assetTypes.IMAGE, this.assetTypes.VIDEO,
        // this.assetTypes.VTT, this.assetTypes.SRT,
        this.assetTypes.SPEECHTOTEXT, this.assetTypes.OTHER
      ];
      return (item) => {
        return systemTypes.indexOf(item.type) >= 0;
      }
    },
    podStates: function() {
      const commonStore = useCommonStore();
      return commonStore.podStates;
    },
    assetState: function() {
      const states = this.podStates;

      let self = this;
      return (item) => {
        if (item.broken) {
          return self.$t('routes.pod.brokenAsset');
        }
        switch (item.state) {
          case states.ERROR: return self.$t('routes.pod.stateError');
          case states.PENDING: return self.$t('routes.pod.statePending');
          case states.PROCESSING: return self.$t('routes.pod.stateProcessing');
          case states.COMPLETE: return self.$t('routes.pod.stateComplete');
          // Instead of the Complete status we show the expiry date
          // case states.COMPLETE: return  this.expiryDate ? `${self.}: ${this.expiryDate}` : '';
        }
      }
    },

    expiryDate: {
      get: function () {
        if (!this.expiryDateValue) {
          return '';
        }
        return formatDate(this.expiryDateValue);
      },
      set: function (value) {
        this.expiryDateValue = value;
      }
    },
    isFormReadonly() {
      return !this.$canListNodeAndPod() || !this.$canControlNodeAndPod();
    },
    getExpirationDateStyle() {
      return function(asset) {
        if (!asset.legalexpirydate) {
          return {};
        }

        const expirationDate = new Date(asset.legalexpirydate);
        if (expirationDate < new Date()) {
          return {
            color: "rgb(var(--v-theme-error)) ",
          };
        }

        return {};
      }
    }
  },
}
</script>

<style lang="scss" scoped>
.pod-asset-list-item {
  border-radius: 10px;
  padding: 8px 4px;
}

.v-theme--light .pod-asset-list-item {
  background-color: #e6ecf1;
}

.v-theme--dark .pod-asset-list-item {
  background-color: #2f3640;
}
</style>
